// style-spec.ts — §STYLE — the versioned, backward-compatible style contract on AppSpec.
//
// THE RULE THIS FILE EXISTS TO ENFORCE: adding a style system to a product with thousands of live
// apps must not change what any existing app looks like. Every existing AppSpec has no `style`
// block, and the ABSENCE of that block means "render exactly as before". There is no implicit
// default pack, because an implicit default would repaint the entire corpus on deploy.
//
// So `resolveStyle()` returns `null` for a legacy spec, and every consumer treats null as "legacy
// path, untouched". A pack is applied only when an app explicitly carries one.
//
// FAIL-SAFE, NEVER FAIL-OPEN-TO-UGLY: an unknown pack id, a drifted version pin, a theme the pack
// does not ship or an intensity it does not declare all degrade to something the pack CAN render,
// and say so in `warnings`. None of them throws, because a styling problem must never take down an
// application that otherwise works.
import REGISTRY from './style-packs.json';

/** The three density contracts. `utility-dense` is the design directory's `utility`. */
export const STYLE_INTENSITIES = ['expressive', 'standard', 'utility-dense'] as const;
export type StyleIntensity = (typeof STYLE_INTENSITIES)[number];

export const STYLE_SCHEMA_VERSION = 1 as const;

export interface StylePackEntry {
  id: string; name: string; version: string;
  themes: string[]; baseTheme: string; intensities: string[];
  /** ISO 15924-ish tags this pack's declared families can actually paint. */
  scripts: string[];
  /** the pack's typographic voice: neutral | serif | heavy | mono | soft */
  scriptVoice: string;
  /**
   * The pack's per-role font declarations, carried in the registry so a WORKER can read them.
   * A worker cannot open style-system/packs at runtime, which is exactly why the exporter shipped
   * Inter for every app — it had no way to know what a pack's typography actually was.
   */
  fonts?: Record<string, { family: string; weights?: number[]; scripts?: string[]; fallback?: string[] }>;
  expressiveOverBudget: boolean; bytes: number;
}
export const STYLE_PACKS: Record<string, StylePackEntry> = (REGISTRY as { packs: Record<string, StylePackEntry> }).packs;
export const STYLE_PACK_IDS: string[] = Object.keys(STYLE_PACKS);

/**
 * The style block as it appears on an AppSpec.
 *
 * Written out explicitly rather than inferred from a schema, because this interface is the contract
 * and the creator must be able to import it without importing a validation library. The zod schema
 * in `style-spec-schema.ts` is asserted — at compile time — to infer exactly this shape.
 */
export interface StyleSpec {
  styleSchemaVersion: typeof STYLE_SCHEMA_VERSION;
  stylePackId: string;
  /**
   * Pinned by CONTENT DIGEST, not a declared semver: a pack that changes its bytes changes its
   * version, so a stale pin is detectable rather than silently re-rendered.
   */
  stylePackVersion: string;
  styleIntensity: StyleIntensity;
  themeMode?: 'light' | 'dark' | 'auto';
  /** semantic role → font family, e.g. { display: 'Fraunces', body: 'Inter' } */
  fontSelections?: Record<string, string>;
  locale?: string;
  /** ISO 15924-ish script tags the app must actually paint, e.g. ['latn','hebr']. */
  requiredScripts?: string[];
  motionPreference?: 'full' | 'reduced' | 'auto';
  adapterConfiguration?: Record<string, unknown>;
}

const PACK_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
const THEME_MODES = ['light', 'dark', 'auto'] as const;
const MOTION_PREFERENCES = ['full', 'reduced', 'auto'] as const;
/** `.strict()` in schema terms: any key not listed here rejects the whole block. */
const STYLE_SPEC_KEYS = new Set([
  'styleSchemaVersion', 'stylePackId', 'stylePackVersion', 'styleIntensity', 'themeMode',
  'fontSelections', 'locale', 'requiredScripts', 'motionPreference', 'adapterConfiguration',
]);

/**
 * A record in schema terms: an object, and specifically NOT an array and NOT a Date.
 *
 * The Date exclusion is not decoration. A Date is `typeof 'object'` with no own enumerable keys, so
 * a naive check accepts it and every per-value rule then passes VACUOUSLY — `{ fontSelections: new
 * Date() }` would validate. The schema oracle classifies a Date as its own type and rejects it, and
 * the generated parity corpus caught exactly this divergence.
 */
function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && !(v instanceof Date);
}
function isStr(v: unknown, min: number, max: number): v is string {
  return typeof v === 'string' && v.length >= min && v.length <= max;
}

/**
 * Validate an unknown value as a StyleSpec, returning it or `null`. Never throws.
 *
 * This is a DEPENDENCY-FREE re-implementation of `styleSpecSchema`, and the two are held to exact
 * agreement by `tests/canonical-style-spec-parity.test.mjs`. It exists because the creator bundle
 * reaches this module: importing a schema library here shipped ~92 KB of validator to every browser
 * that opens the style picker, which is measured in `evidence/budget/bundle.json`.
 *
 * An optional key may be ABSENT or explicitly `undefined` — both are accepted, matching the schema.
 */
export function parseStyleSpec(raw: unknown): StyleSpec | null {
  if (!isRecord(raw)) return null;
  for (const k of Object.keys(raw)) if (!STYLE_SPEC_KEYS.has(k)) return null;

  if (raw.styleSchemaVersion !== STYLE_SCHEMA_VERSION) return null;
  if (typeof raw.stylePackId !== 'string' || !PACK_ID_RE.test(raw.stylePackId)) return null;
  if (!isStr(raw.stylePackVersion, 8, 64)) return null;
  if (!STYLE_INTENSITIES.includes(raw.styleIntensity as StyleIntensity)) return null;

  const out: StyleSpec = {
    styleSchemaVersion: STYLE_SCHEMA_VERSION,
    stylePackId: raw.stylePackId,
    stylePackVersion: raw.stylePackVersion,
    styleIntensity: raw.styleIntensity as StyleIntensity,
  };

  if (raw.themeMode !== undefined) {
    if (!THEME_MODES.includes(raw.themeMode as (typeof THEME_MODES)[number])) return null;
    out.themeMode = raw.themeMode as 'light' | 'dark' | 'auto';
  }
  if (raw.fontSelections !== undefined) {
    if (!isRecord(raw.fontSelections)) return null;
    for (const v of Object.values(raw.fontSelections)) if (!isStr(v, 1, 64)) return null;
    out.fontSelections = raw.fontSelections as Record<string, string>;
  }
  if (raw.locale !== undefined) {
    if (!isStr(raw.locale, 2, 35)) return null;
    out.locale = raw.locale;
  }
  if (raw.requiredScripts !== undefined) {
    const rs = raw.requiredScripts;
    if (!Array.isArray(rs) || rs.length > 24) return null;
    for (const v of rs) if (!isStr(v, 2, 8)) return null;
    out.requiredScripts = rs as string[];
  }
  if (raw.motionPreference !== undefined) {
    if (!MOTION_PREFERENCES.includes(raw.motionPreference as (typeof MOTION_PREFERENCES)[number])) return null;
    out.motionPreference = raw.motionPreference as 'full' | 'reduced' | 'auto';
  }
  if (raw.adapterConfiguration !== undefined) {
    if (!isRecord(raw.adapterConfiguration)) return null;
    out.adapterConfiguration = raw.adapterConfiguration;
  }
  return out;
}


export interface ResolvedStyle {
  packId: string; packName: string;
  /** the version actually applied */
  version: string;
  /** the version the app asked for — differs from `version` when the pack drifted */
  pinnedVersion: string;
  intensity: StyleIntensity;
  themeMode: 'light' | 'dark' | 'auto';
  fontSelections: Record<string, string>;
  locale: string | null;
  requiredScripts: string[];
  motionPreference: 'full' | 'reduced' | 'auto';
  adapterConfiguration: Record<string, unknown>;
  /** true when the pack declares this intensity is over the frame budget (Liquid Glass expressive) */
  overBudget: boolean;
  /** every degradation applied, in the order applied. Empty means the request was honoured exactly. */
  warnings: string[];
}

/**
 * Resolve an AppSpec's style block.
 *
 * Returns `null` when the spec carries no style — the LEGACY path, byte-identical to today.
 * Never throws.
 */
export function resolveStyle(spec: unknown): ResolvedStyle | null {
  const raw = (spec as { style?: unknown } | null | undefined)?.style;
  if (raw == null) return null;

  const s0 = parseStyleSpec(raw);
  if (s0 === null) {
    // A malformed style block is not a reason to render nothing, and it is not a reason to invent a
    // pack either. Legacy is the only safe floor.
    return null;
  }
  const s = s0;
  const warnings: string[] = [];

  const pack = STYLE_PACKS[s.stylePackId];
  if (!pack) {
    // The pack was removed or never existed. Falling back to another pack would silently restyle
    // somebody's product into a look they never chose, so this degrades to legacy.
    return null;
  }

  let version = pack.version;
  if (s.stylePackVersion !== pack.version) {
    warnings.push(
      `pinned ${s.stylePackId}@${s.stylePackVersion} but the installed pack is @${pack.version} — rendering the installed one`,
    );
  }

  let intensity: StyleIntensity = s.styleIntensity;
  if (!pack.intensities.includes(intensity)) {
    warnings.push(`${s.stylePackId} does not declare intensity ${intensity} — using standard`);
    intensity = 'standard';
  }

  let themeMode = s.themeMode ?? 'auto';
  if (themeMode !== 'auto' && !pack.themes.includes(themeMode)) {
    warnings.push(`${s.stylePackId} has no ${themeMode} theme — using its base theme ${pack.baseTheme}`);
    themeMode = pack.baseTheme as 'light' | 'dark';
  }

  const overBudget = pack.expressiveOverBudget && intensity === 'expressive';
  if (overBudget) {
    warnings.push(
      `${s.stylePackId} in expressive intensity is measured over the frame budget and is opt-in; the runtime degrades effects under load or reduced motion`,
    );
  }

  return {
    packId: pack.id, packName: pack.name,
    version, pinnedVersion: s.stylePackVersion,
    intensity, themeMode,
    fontSelections: s.fontSelections ?? {},
    locale: s.locale ?? null,
    requiredScripts: s.requiredScripts ?? [],
    motionPreference: s.motionPreference ?? 'auto',
    adapterConfiguration: s.adapterConfiguration ?? {},
    overBudget,
    warnings,
  };
}

/** Build a valid, correctly pinned style block for a pack. Returns null for an unknown pack. */
export function makeStyleSpec(
  packId: string,
  opts: Partial<Omit<StyleSpec, 'styleSchemaVersion' | 'stylePackId' | 'stylePackVersion'>> = {},
): StyleSpec | null {
  const pack = STYLE_PACKS[packId];
  if (!pack) return null;
  const intensity = opts.styleIntensity && pack.intensities.includes(opts.styleIntensity)
    ? opts.styleIntensity : 'standard';
  const out: StyleSpec = {
    styleSchemaVersion: STYLE_SCHEMA_VERSION,
    stylePackId: pack.id,
    stylePackVersion: pack.version,
    styleIntensity: intensity,
  };
  if (opts.themeMode) out.themeMode = opts.themeMode;
  if (opts.fontSelections) out.fontSelections = opts.fontSelections;
  if (opts.locale) out.locale = opts.locale;
  if (opts.requiredScripts) out.requiredScripts = opts.requiredScripts;
  if (opts.motionPreference) out.motionPreference = opts.motionPreference;
  if (opts.adapterConfiguration) out.adapterConfiguration = opts.adapterConfiguration;
  return out;
}

/**
 * Apply a pack to a spec, returning a NEW spec plus the exact token needed to undo it.
 *
 * Reversibility is the whole point: `rollbackStyle(applied.spec, applied.previous)` must restore the
 * ORIGINAL object, including the case where the original had no style at all. Business data and
 * behaviour are never touched — only the `style` key.
 */
export function applyStyle<T extends Record<string, unknown>>(
  spec: T, packId: string, opts?: Parameters<typeof makeStyleSpec>[1],
): { spec: T; previous: StyleSpec | null; changed: boolean } | null {
  const style = makeStyleSpec(packId, opts);
  if (!style) return null;
  const previous = (spec.style as StyleSpec | undefined) ?? null;
  const next = { ...spec, style } as T;
  return { spec: next, previous, changed: JSON.stringify(previous) !== JSON.stringify(style) };
}

/** Undo applyStyle. `previous: null` removes the block entirely, restoring the legacy render. */
export function rollbackStyle<T extends Record<string, unknown>>(spec: T, previous: StyleSpec | null): T {
  const next = { ...spec } as Record<string, unknown>;
  if (previous == null) delete next.style; else next.style = previous;
  return next as T;
}
